import { useMemo } from "react";
import type { CSSProperties } from "react";
import type { Branding } from "./api/types";
import { readableOn, shade, tint, withAlpha } from "./color";

/** Fallback brand colour when a tenant hasn't set one. */
export const DEFAULT_BRAND = "#10b981";

/** Neutral accent used ONLY while branding is still loading, so the UI preloads in grey and the tenant's
 *  real colour fades in — rather than flashing the green default first. */
export const NEUTRAL_BRAND = "#64748b";

export interface BrandPalette {
  brand: string;
  brandHover: string;
  brandActive: string;
  brandTint: string;
  brandTintStrong: string;
  onBrand: string;
  ring: string;
}

/** Derive a full brand ramp from a single hex (or the default when null/invalid). */
export function deriveBrandPalette(hex?: string | null): BrandPalette {
  const brand = hex && hex.trim() ? hex.trim() : DEFAULT_BRAND;
  return {
    brand,
    brandHover: shade(brand, 0.1),
    brandActive: shade(brand, 0.18),
    brandTint: tint(brand, 0.9),
    brandTintStrong: tint(brand, 0.82),
    onBrand: readableOn(brand),
    ring: withAlpha(brand, 0.35),
  };
}

/**
 * CSS custom properties for a palette plus the static neutral / radius / shadow tokens.
 * Spread onto a wrapper's `style` so every descendant can reference `var(--brand)` etc.
 */
export function brandCssVars(p: BrandPalette): CSSProperties {
  // Custom properties (`--*`) aren't part of the CSSProperties interface, so build a plain
  // string map and cast through it.
  const vars: Record<string, string> = {
    "--brand": p.brand,
    "--brand-hover": p.brandHover,
    "--brand-active": p.brandActive,
    "--brand-tint": p.brandTint,
    "--brand-tint-strong": p.brandTintStrong,
    "--on-brand": p.onBrand,
    "--ring": p.ring,

    "--bg": "#f6f7f9",
    "--surface": "#ffffff",
    "--border": "#e7e9ee",
    "--border-strong": "#d6dae2",
    "--text": "#101317",
    // Two muted levels, both kept at/above WCAG AA (4.5:1) on --surface so helper/hint text stays legible
    // (e.g. on a sunlit phone). --muted-2 was #9aa3af (~2.6:1) and failed AA.
    "--muted": "#5b616b",
    "--muted-2": "#6b7280",
    "--danger": "#dc2626",

    "--radius-sm": "8px",
    "--radius": "12px",
    "--radius-lg": "16px",
    "--radius-pill": "999px",

    "--shadow-sm": "0 1px 2px rgba(16, 24, 40, 0.06)",
    "--shadow-md": "0 4px 12px rgba(16, 24, 40, 0.08)",
    "--shadow-lg": "0 12px 32px rgba(16, 24, 40, 0.16)",
  };
  return vars as CSSProperties;
}

/** Memoised palette for components that need concrete colour values (e.g. SVG strokes that can't read CSS vars). */
export function useBrandTheme(branding: Branding | null | undefined): BrandPalette {
  return useMemo(() => deriveBrandPalette(branding?.brandPrimaryColor), [branding?.brandPrimaryColor]);
}
